import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import {
  MDSButton,
  MDSButtonSize,
  MDSButtonVariant,
  MDSCard,
  MDSCardVariant,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import { AgentLinkButton } from "@/pages/agent/components/agent-link-button";
import { AgentLinkedTaskList } from "@/pages/agent/components/agent-linked-task-list";
import { AgentPageLayout } from "@/pages/agent/components/agent-page-layout";
import { AgentSourceNoteCard } from "@/pages/agent/components/agent-source-note-card";
import { AgentSurfaceNavigationActiveTab } from "@/pages/agent/components/agent-surface-navigation";
import type { AgentInformationItem } from "@/pages/agent/intent-graph/agent-intent-graph.types";
import { useAgentIntentGraph } from "@/pages/agent/intent-graph/use-agent-intent-graph";
import type { TaskSourceNote } from "@/pages/agent/utils/agent-task-source.utils";
import { useAppStore } from "@/store";
import type { AssistantTaskObservable } from "@/store/assistant";
import {
  isClosedAssistantTaskStatus,
  normalizeAssistantTaskStatus,
} from "@/store/assistant/assistant-task-status";
import { AssistantTaskStatus } from "@/store/assistant/types";

type LifecycleFilter = "all" | "active" | "archived";

export const AgentInformationPage = observer(() => {
  const { store } = useAppStore();
  const graph = useAgentIntentGraph();
  const [filter, setFilter] = useState<LifecycleFilter>("all");
  const items = (graph.data?.information_items ?? []).filter(
    item => filter === "all" || item.lifecycle === filter
  );
  const openTask = ({ taskId }: { taskId: string }) => {
    store.agentOverlay.openTask({ taskId });
  };
  const toggleTaskComplete = async ({ taskId }: { taskId: string }) => {
    const task = store.assistantTasks.getCurrentAssistantModelById(taskId);
    if (!task) return;

    const status = normalizeAssistantTaskStatus({ status: task.status });
    const nextStatus = isClosedAssistantTaskStatus({ status })
      ? AssistantTaskStatus.Pending
      : AssistantTaskStatus.Completed;

    await task.updateStatus({ status: nextStatus });
  };

  return (
    <AgentPageLayout activeTab={AgentSurfaceNavigationActiveTab.Information}>
      <StyledHeader>
        <MDSText
          color={MDSTextColor.Primary}
          variant={MDSTextVariant.HeaderM}
          weight={MDSTextWeight.SemiBold}
        >
          Information
        </MDSText>
        <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.ParagraphS}>
          Notes and connected records Mem is actively tracking or has archived.
        </MDSText>
      </StyledHeader>
      <StyledFilters aria-label="Information lifecycle">
        {(["all", "active", "archived"] as const).map(value => (
          <MDSButton
            key={value}
            size={MDSButtonSize.Small}
            variant={filter === value ? MDSButtonVariant.Chrome : MDSButtonVariant.Transparent}
            onPress={() => setFilter(value)}
          >
            {value === "archived" ? "Archived" : value[0].toUpperCase() + value.slice(1)}
          </MDSButton>
        ))}
      </StyledFilters>
      {graph.isLoading ? (
        <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS}>
          Loading information…
        </MDSText>
      ) : graph.isError ? (
        <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS}>
          Information could not be loaded.
        </MDSText>
      ) : items.length === 0 ? (
        <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS}>
          No {filter === "all" ? "information" : filter} items.
        </MDSText>
      ) : (
        <StyledList>
          {items.map(item => (
            <InformationCard
              key={item.id}
              item={item}
              note={
                item.kind === "note"
                  ? store.notes.getNoteObservableById({ noteId: item.locator })
                  : undefined
              }
              tasks={(item.task_ids ?? []).flatMap(taskId => {
                const task = store.assistantTasks.getCurrentAssistantModelById(taskId);
                return task ? [task] : [];
              })}
              onOpenDetails={({ informationItemId }) =>
                store.agentOverlay.openInformationItem({ informationItemId })
              }
              onOpenNote={({ noteId }) => store.agentOverlay.openSourceNote({ noteId })}
              onOpenTask={openTask}
              onToggleTaskComplete={toggleTaskComplete}
            />
          ))}
        </StyledList>
      )}
    </AgentPageLayout>
  );
});

const InformationCard = ({
  item,
  note,
  tasks,
  onOpenDetails,
  onOpenNote,
  onOpenTask,
  onToggleTaskComplete,
}: {
  item: AgentInformationItem;
  note: TaskSourceNote | null | undefined;
  tasks: AssistantTaskObservable[];
  onOpenDetails: ({ informationItemId }: { informationItemId: string }) => void;
  onOpenNote: ({ noteId }: { noteId: string }) => void;
  onOpenTask: ({ taskId }: { taskId: string }) => void;
  onToggleTaskComplete: ({ taskId }: { taskId: string }) => void;
}) => (
  <StyledItemCard variant={MDSCardVariant.Chrome}>
    <StyledItemHeader>
      <StyledDescriptor
        color={MDSTextColor.Primary}
        variant={MDSTextVariant.TextM}
        weight={MDSTextWeight.SemiBold}
      >
        {item.descriptor}
      </StyledDescriptor>
      <StyledLifecycle $archived={item.lifecycle === "archived"}>
        {item.lifecycle === "archived" ? "Archived" : "Active"}
      </StyledLifecycle>
    </StyledItemHeader>

    {item.kind === "note" && note?.isAvailable ? (
      <AgentSourceNoteCard note={note} onOpen={onOpenNote} />
    ) : (
      <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.ParagraphS}>
        {item.kind === "note" ? "Source note unavailable" : `Connected ${item.kind}`}
      </MDSText>
    )}

    <StyledLinkedTasks>
      <MDSText
        color={MDSTextColor.Primary}
        variant={MDSTextVariant.TextS}
        weight={MDSTextWeight.SemiBold}
      >
        Linked tasks
      </MDSText>
      <AgentLinkedTaskList
        tasks={tasks}
        onOpenTask={onOpenTask}
        onToggleTaskComplete={onToggleTaskComplete}
      />
    </StyledLinkedTasks>

    <StyledDetailsLink onClick={() => onOpenDetails({ informationItemId: item.id })}>
      View tracking details
    </StyledDetailsLink>
  </StyledItemCard>
);

const StyledHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const StyledFilters = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const StyledList = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const StyledItemCard = styled(MDSCard)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const StyledDescriptor = styled(MDSText)`
  overflow-wrap: anywhere;
`;

const StyledItemHeader = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  justify-content: space-between;
`;

const StyledLifecycle = styled.span<{ $archived: boolean }>`
  background: ${({ theme, $archived }) =>
    $archived ? theme.colors.background.menu.default : theme.colors.background.banner.default};
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes[MDSTextVariant.TextXs]};
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[2]};
`;

const StyledLinkedTasks = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const StyledDetailsLink = styled(AgentLinkButton)`
  align-self: flex-start;
`;
